/**
 * The US National Weather Service heat index, exactly as the Weather Prediction Center
 * documents it (wpc.ncep.noaa.gov/html/heatindex_equation.shtml).
 *
 * 1. Compute Steadman's simple formula, HI = 0.5 · {T + 61.0 + [(T − 68.0) · 1.2] + 0.094 RH},
 *    average it with the air temperature T, and if that is 80 °F or more:
 * 2. Use the Rothfusz multiple regression (NWS Technical Attachment SR 90-23) with
 *    T in °F and RH in percent, then apply the adjustments:
 *    - RH < 13 % and 80 °F ≤ T ≤ 112 °F: subtract [(13 − RH) / 4] · √{[17 − |T − 95|] / 17}
 *    - RH > 85 % and 80 °F ≤ T ≤ 87 °F: add [(RH − 85) / 10] · [(87 − T) / 5]
 *
 * Values assume shade and light wind; full sun can raise the felt value by up to 15 °F
 * (NWS). The regression is not valid beyond the range of data Steadman considered.
 */

import {
  fromCelsius,
  toCelsius,
} from "@/lib/tool-kit/earth-science/temperature";

export type HeatIndexMethod = "simple" | "regression";

export interface HeatIndexResult {
  /** The heat index in °F, unrounded. */
  fahrenheit: number;
  method: HeatIndexMethod;
  /** Steadman's simple formula before averaging. */
  simple: number;
  /** The simple value averaged with the air temperature. */
  averaged: number;
  /** The Rothfusz regression before any adjustment; null when it was not used. */
  regression: number | null;
  /** Signed amount added to the regression: negative for the low-RH rule. */
  adjustment: number;
  adjustmentKind: "low" | "high" | null;
}

/** Steadman's simple formula (°F, %). */
export function simpleHeatIndex(t: number, rh: number): number {
  return 0.5 * (t + 61 + (t - 68) * 1.2 + rh * 0.094);
}

/** The Rothfusz regression (°F, %), unadjusted. */
export function rothfuszRegression(t: number, rh: number): number {
  return (
    -42.379 +
    2.049_015_23 * t +
    10.143_331_27 * rh -
    0.224_755_41 * t * rh -
    0.006_837_83 * t * t -
    0.054_817_17 * rh * rh +
    0.001_228_74 * t * t * rh +
    0.000_852_82 * t * rh * rh -
    0.000_001_99 * t * t * rh * rh
  );
}

/** The NWS low- and high-humidity adjustments: a signed amount to add, and which rule. */
export function heatIndexAdjustment(
  t: number,
  rh: number
): { amount: number; kind: "low" | "high" | null } {
  if (rh < 13 && t >= 80 && t <= 112) {
    return {
      amount: -((13 - rh) / 4) * Math.sqrt((17 - Math.abs(t - 95)) / 17),
      kind: "low",
    };
  }
  if (rh > 85 && t >= 80 && t <= 87) {
    return { amount: ((rh - 85) / 10) * ((87 - t) / 5), kind: "high" };
  }
  return { amount: 0, kind: null };
}

/** The heat index for `t` °F and `rh` percent, following the NWS procedure step by step. */
export function heatIndex(t: number, rh: number): HeatIndexResult {
  const simple = simpleHeatIndex(t, rh);
  const averaged = (simple + t) / 2;
  if (averaged < 80) {
    return {
      fahrenheit: averaged,
      method: "simple",
      simple,
      averaged,
      regression: null,
      adjustment: 0,
      adjustmentKind: null,
    };
  }
  const regression = rothfuszRegression(t, rh);
  const { amount, kind } = heatIndexAdjustment(t, rh);
  return {
    fahrenheit: regression + amount,
    method: "regression",
    simple,
    averaged,
    regression,
    adjustment: amount,
    adjustmentKind: kind,
  };
}

/** The NWS classification, by heat index in °F (whole degrees, as the NWS reports it). */
export type HeatIndexCategory =
  | "belowRange"
  | "caution"
  | "extremeCaution"
  | "danger"
  | "extremeDanger";

export function heatIndexCategory(fahrenheit: number): HeatIndexCategory {
  const value = Math.round(fahrenheit);
  if (value < 80) {
    return "belowRange";
  }
  if (value < 90) {
    return "caution";
  }
  if (value < 103) {
    return "extremeCaution";
  }
  return value < 125 ? "danger" : "extremeDanger";
}

/** Convenience for the widget: a reading in either unit, the result in both. */
export function heatIndexFrom(
  temperature: number,
  unit: "F" | "C",
  rh: number
): HeatIndexResult & { celsius: number; inputFahrenheit: number } {
  const inputFahrenheit = fromCelsius(toCelsius(temperature, unit), "F");
  const result = heatIndex(inputFahrenheit, rh);
  return {
    ...result,
    inputFahrenheit,
    celsius: toCelsius(result.fahrenheit, "F"),
  };
}
